/**
 * BundleCommand.js
 * Wire bundle oluşturma ve genişletme undo/redo komutu.
 *
 * action: 'CREATE' | 'EXPAND'
 *
 * CREATE:
 *   execute/redo  → bundle grubunu sahneye ekle, wire'ların kısaltılmış geometrisini uygula
 *   undo          → orijinal wire geometrilerini geri yükle, bundle grubunu ve extra line'ları sahneden kaldır
 *
 * EXPAND:
 *   execute/redo  → orijinal wire geometrilerini geri yükle, bundle grubunu ve extra line'ları sahneden kaldır
 *   undo          → kısaltılmış geometrileri geri uygula, bundle grubunu ve extra line'ları sahneye geri ekle
 *
 * Önemli: bundleGroup ve extraLines asla dispose edilmez; komut geçmişi boyunca canlı tutulur.
 */

import BaseCommand from './BaseCommand';
import * as THREE from 'three';

class BundleCommand extends BaseCommand {
  constructor(action, bundleGroup, wireBeforeStates, wireAfterStates, extraLines, markerUuids) {
    super(`BUNDLE_${action}`, { before: null, after: null });
    this.action = action;
    this._bundleGroup = bundleGroup;
    this._wireBeforeStates = wireBeforeStates; // [{wireId, positions:[number]}]
    this._wireAfterStates = wireAfterStates;   // [{wireId, positions:[number]}]
    this._extraLines = extraLines;             // THREE.Line[] — stub line'lar
    this._markerUuids = markerUuids;           // string[] — gizlenen wire marker UUID'leri
  }

  execute(scene, dispatch) {
    if (this.action === 'CREATE') {
      // Redo: kısaltılmış geometrileri uygula, bundle'ı ve extra line'ları geri ekle
      this._applyWireStates(scene, this._wireAfterStates);
      this._setMarkersVisible(scene, false);
      this._extraLines.forEach(el => { if (!el.parent) scene.add(el); });
      if (!this._bundleGroup.parent) scene.add(this._bundleGroup);
    } else if (this.action === 'EXPAND') {
      // Redo: orijinal geometrileri geri yükle, bundle'ı ve extra line'ları kaldır
      this._applyWireStates(scene, this._wireAfterStates);
      this._setMarkersVisible(scene, true);
      this._extraLines.forEach(el => { if (el.parent) el.parent.remove(el); });
      if (this._bundleGroup.parent) this._bundleGroup.parent.remove(this._bundleGroup);
      else scene.remove(this._bundleGroup);
    }
  }

  undo(scene, dispatch) {
    if (this.action === 'CREATE') {
      // Undo: orijinal geometrileri geri yükle, bundle'ı ve extra line'ları kaldır
      this._applyWireStates(scene, this._wireBeforeStates);
      this._setMarkersVisible(scene, true);
      this._extraLines.forEach(el => { if (el.parent) el.parent.remove(el); });
      if (this._bundleGroup.parent) this._bundleGroup.parent.remove(this._bundleGroup);
      else scene.remove(this._bundleGroup);
    } else if (this.action === 'EXPAND') {
      // Undo: kısaltılmış geometrileri geri uygula, bundle'ı ve extra line'ları geri ekle
      this._applyWireStates(scene, this._wireBeforeStates);
      this._setMarkersVisible(scene, false);
      this._extraLines.forEach(el => { if (!el.parent) scene.add(el); });
      if (!this._bundleGroup.parent) scene.add(this._bundleGroup);
    }
  }

  _applyWireStates(scene, wireStates) {
    const map = new Map(wireStates.map(ws => [ws.wireId, ws.positions]));
    scene.traverse(obj => {
      if (!obj.isLine) return;
      const id = obj.userData?.id;
      if (!id || !map.has(id)) return;
      const positions = map.get(id);
      const arr = new Float32Array(positions);
      obj.geometry.setAttribute('position', new THREE.BufferAttribute(arr, 3));
      obj.geometry.attributes.position.needsUpdate = true;
      obj.geometry.computeBoundingSphere();
      if (typeof obj.computeLineDistances === 'function') obj.computeLineDistances();
    });
  }

  _setMarkersVisible(scene, visible) {
    if (this._markerUuids.length === 0) return;
    const uuidSet = new Set(this._markerUuids);
    scene.traverse(obj => {
      if (uuidSet.has(obj.uuid)) obj.visible = visible;
    });
  }
}

export default BundleCommand;
